import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useMemo,
} from 'react';

import { Collapse, styled, useTheme } from '@mui/material';
import kebabCase from 'lodash/kebabCase';
import { useLocation } from 'react-router-dom';

import {
  activeProps,
  ConditionalWrapper,
  focusProps,
  Icons,
  ProgressBar,
  SxProps,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { useDemoState, useIsOpDemoMode } from '@cast/infrastructure-demo';
import { Link as RouterLink } from 'components/router';
import {
  Ability,
  AbilityConditions,
  Actions,
  Subjects,
  useAbility,
} from '@cast/shared-ability-data-access';
import { useIsOrganizationReadOnly } from 'hooks/useIsOrganizationReadOnly';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import type { BoxProps } from '@cast/design-system';

type OwnerState = {
  isSubItem: boolean;
  isDisabled: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  isChildSelected: boolean;
  hasChildren: boolean;
};

type MenuBoxProps = BoxProps & {
  ownerState: OwnerState;
};

const MenuBox = styled(Box, {
  shouldForwardProp: (propName: PropertyKey) => propName !== 'ownerState',
})<MenuBoxProps>(({ theme, ownerState }) => [
  {
    marginLeft: ownerState.isSubItem ? '8px' : undefined,
    textDecoration: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '4px',
    minHeight: '32px',
    border: '1px solid transparent',
    color: theme.palette.grey[600],

    '&:hover': {
      cursor:
        ownerState.hasChildren && ownerState.isExpanded ? 'default' : 'pointer',
      backgroundColor:
        ownerState.hasChildren && ownerState.isExpanded
          ? 'transparent'
          : theme.palette.grey[200],
    },
  },
  !ownerState.isDisabled &&
    !ownerState.isExpanded && {
      '&&:active': {
        color: theme.palette.grey[600],
        backgroundColor: theme.palette.blue[100],
        ...activeProps(theme),
      },

      '&:focus-visible': focusProps(),
    },
  ownerState.isDisabled && {
    '& > div': {
      color: theme.palette.grey[300],
    },
    '&:hover': {
      cursor: 'default',
      backgroundColor: 'none',
    },
  },
  ownerState.isExpanded && {
    '& svg line': {
      stroke: theme.palette.blue[500],
    },
    '& span': {
      color: theme.palette.blue[500],
    },
  },
  ownerState.isSelected &&
    !ownerState.hasChildren && {
      backgroundColor: theme.palette.blue[100],
      '& > div': {
        color: theme.palette.blue[600],
      },
      '&:hover': {
        backgroundColor: theme.palette.blue[200],
      },
    },
  ownerState.isSelected &&
    ownerState.hasChildren && {
      color: theme.palette.blue[500],
    },
]);

export type SidebarItemProps = {
  title: string;
  url?: string;
  /**
   * Extra path prefixes that mark this item selected (e.g. Platform usage →
   * classic `org-billable-cpus` plus reporting `billing-report/*`).
   */
  activeMatchUrls?: string[];
  disabled?: boolean;
  isSubItem?: boolean;
  tooltip?: string;
  endAdornment?: ReactNode;
  progressBar?: { show: boolean; value: number };
  rules?: Array<[Actions, Subjects]>;
  rulesConditions?: AbilityConditions;
  supportsDemo?: boolean;
  childrenWrapperSx?: SxProps;
  children?: ReactNode;
  testId?: string;
  expanded?: boolean;
  allowedInReadOnlyOrg?: boolean;
};

const getPathFromUrl = (url: string) => {
  return url.split(/[?#]/)[0];
};

const hasRenderableChildren = (
  children: any,
  rulesConditions: AbilityConditions | undefined,
  ability: Ability
): boolean => {
  return (Array.isArray(children) ? children : [children]).some((child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      return true;
    }

    if (isValidElement(child)) {
      if (child.type === Fragment) {
        const props = child.props as PropsWithChildren<unknown>;
        return hasRenderableChildren(props.children, rulesConditions, ability);
      }

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      if (child.type === SidebarItem) {
        const props = child.props as SidebarItemProps;

        // Note: This function is called from within render context, so we use store getter
        const isDemoMode = useDemoState.getState().getIsClusterDemoMode();
        if (isDemoMode && !props.supportsDemo) {
          return false;
        }

        return props.rules?.length
          ? ability.canMany(props.rules, rulesConditions).every((v) => v)
          : true;
      }

      return true;
    }

    return false;
  });
};

const getChildItems = (
  children: ReactNode
): ReactElement<SidebarItemProps>[] => {
  const result: ReactElement<SidebarItemProps>[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement<SidebarItemProps>(child)) {
      return;
    }
    if (child.type === Fragment) {
      result.push(...getChildItems(child.props.children));
    } else {
      result.push(
        cloneElement(child, {
          ...child.props,
          isSubItem: true,
        })
      );
    }
  });
  return result;
};

const appendOrganizationIdToUrl = (
  url: string | undefined,
  organizationId: string | undefined
) => {
  if (!url) return null;
  const urlWithOrg = new URL(url, window.location.origin);
  if (organizationId) {
    urlWithOrg.searchParams.set('org', organizationId);
  }
  return `${urlWithOrg.pathname}${urlWithOrg.search}`;
};

const pathMatchesLocation = (pathname: string, url: string | undefined) =>
  Boolean(url && pathname.startsWith(getPathFromUrl(url)));

export const SidebarItem = ({
  children,
  isSubItem,
  title,
  url,
  activeMatchUrls,
  disabled,
  tooltip,
  endAdornment,
  progressBar,
  rules,
  rulesConditions,
  supportsDemo = true,
  testId,
  expanded,
  allowedInReadOnlyOrg,
}: PropsWithChildren<SidebarItemProps>) => {
  const theme = useTheme();
  const location = useLocation();
  const { currentOrganization } = useOrganizations();
  const ability = useAbility();
  const isOpDemo = useIsOpDemoMode();
  const isOrganizationReadOnly = useIsOrganizationReadOnly();

  const isDisabled =
    !allowedInReadOnlyOrg && (disabled || isOrganizationReadOnly);

  const _children = getChildItems(children);

  const isChildSelected = useMemo(() => {
    for (const child of _children) {
      if (
        pathMatchesLocation(location.pathname, child.props.url) ||
        child.props.activeMatchUrls?.some((matchUrl) =>
          pathMatchesLocation(location.pathname, matchUrl)
        )
      ) {
        return true;
      }
    }
    return false;
  }, [location.pathname, _children]);

  const isSelected =
    isChildSelected ||
    pathMatchesLocation(location.pathname, url) ||
    Boolean(
      activeMatchUrls?.some((matchUrl) =>
        pathMatchesLocation(location.pathname, matchUrl)
      )
    );

  const isExpanded = expanded || isChildSelected;

  const ownerState: OwnerState = {
    isSubItem: Boolean(isSubItem),
    isDisabled,
    isExpanded,
    isSelected,
    isChildSelected,
    hasChildren: Boolean(children),
  };

  const doesntHaveAccess =
    rules?.length && ability.canMany(rules, rulesConditions).some((v) => !v);
  const noRenderableChildren =
    _children.length &&
    !hasRenderableChildren(_children, rulesConditions, ability);

  if (isOpDemo) {
    if (!supportsDemo || doesntHaveAccess) return null;
  } else {
    if (doesntHaveAccess || noRenderableChildren) return null;
  }

  const parentUrl = appendOrganizationIdToUrl(url, currentOrganization?.id);
  const sidebarItemTestId = `sidebar-item-${testId ?? kebabCase(title)}`;

  return (
    <Stack>
      <ConditionalWrapper
        condition={!!tooltip}
        wrapper={(children) => (
          <Tooltip
            arrow
            size="small"
            title={tooltip as string}
            placement="right"
          >
            <div>{children}</div>
          </Tooltip>
        )}
      >
        <MenuBox
          ownerState={ownerState}
          data-testid={sidebarItemTestId}
          data-enabled={!isDisabled}
          aria-level={children ? 1 : 2}
          aria-selected={isSelected}
          {...(parentUrl && !isDisabled && !ownerState.isExpanded
            ? {
                component: RouterLink,
                to: parentUrl,
              }
            : {})}
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={8}
              position="relative"
              width="100%"
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  bottom: 0,
                  left: 0,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={6}
                  pr={ownerState.hasChildren ? 4 : 0}
                >
                  <Stack
                    display="inline-flex"
                    direction="row"
                    alignItems="center"
                    gap={6}
                    position="relative"
                  >
                    <Typography variant="P14M" component="div">
                      {title}
                    </Typography>
                    {tooltip && <Icons.Info size={16} />}
                    {endAdornment}
                  </Stack>
                </Stack>

                {progressBar?.show && (
                  <ProgressBar
                    data-testid={`${sidebarItemTestId}-progress-bar`}
                    sx={{
                      position: 'absolute',
                      bottom: '-2px',
                      left: '0',
                      width: '100%',

                      ...(isSelected && {
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.blue[600],
                        },
                      }),
                    }}
                    size="sm"
                    color="tertiary"
                    variant="determinate"
                    value={progressBar.value}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {children && (
            <Icons.WithRotation rotated={!isExpanded}>
              <Icons.CaretUp
                size={16}
                color={
                  isSelected ? theme.palette.blue[500] : theme.palette.grey[600]
                }
              />
            </Icons.WithRotation>
          )}
        </MenuBox>
      </ConditionalWrapper>
      <Collapse in={!!children && isExpanded} mountOnEnter unmountOnExit>
        <Box position="relative" display="flex" ml={8} mt={4}>
          <Box
            className="sidebar-children-line"
            position="absolute"
            top="0"
            left="0"
            bottom="0px"
            borderLeft="1px solid"
            borderColor={tokens.theme.border.structural.neutral}
          />

          <Stack flexGrow={1} gap="4px">
            {_children.map((child, index) => (
              <Fragment key={index}>{child}</Fragment>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Stack>
  );
};
